'use client';

import React from 'react';
import {
  Container,
  Title,
  Text,
  Grid,
  Card,
  Group,
  Stack,
  Badge,
  Tooltip,
  Box,
  Image,
  Paper,
  useMantineTheme,
  Modal
} from '@mantine/core';
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import FAIcon, { ValidIconName } from '@/components/FAIcon';
import { useResponsive } from '@/hooks/use-responsive';
import { useDisclosure } from '@mantine/hooks';
import SiteHeader from '@/components/SiteHeader';
import CustomCarousel from '@/components/CustomCarousel';
import CustomAccordion from '@/components/Accordion';
import { useAuthContext } from '@/context/Auth';
import Button from '@/components/Button';
import SiteFooter from '@/components/SiteFooter';
import HelpChangeSection from '@/components/HelpChangeSection';

const socialPosts = [
  {
    content: "Transform your condo document review process with HomeHealth Solutions! 🏢✨ #RealEstate #PropTech",
    image: "/img/draft-landing-page/social-1.png", // TODO: Replace with actual image
    hashtags: ['RealEstate', 'PropTech']
  },
  {
    content: "Save time and money with our streamlined document review service! 💰⏱️ #PropertyManagement #Efficiency",
    image: "/img/draft-landing-page/social-2.png",
    hashtags: ['PropertyManagement', 'Efficiency']
  },
  {
    content: "Expert reviews, faster closings, happier clients! 🎯🤝 #RealEstateSuccess #CondoExperts",
    image: "/img/draft-landing-page/social-3.png",
    hashtags: ['RealEstateSuccess', 'CondoExperts']
  }
];

interface DocumentType {
  title: string;
  importance: 'high' | 'medium' | 'low';
  items: string[];
  description: string;
  icon: ValidIconName;
}

const documentTypes: DocumentType[] = [
  {
    title: 'Financial Documents',
    importance: 'high',
    items: ['Budget', 'Reserve Study', 'Financial Statements', 'Audit Reports', 'Tax Returns', 'Insurance Policies', 'Assessment Schedule'],
    description: 'Critical financial records that detail the association\'s fiscal health',
    icon: 'chart-line'
  },
  {
    title: 'Legal Documents',
    importance: 'high',
    items: ['Bylaws', 'CC&Rs', 'Articles of Incorporation', 'Rules and Regulations', 'Amendments', 'Meeting Minutes', 'Resolutions'],
    description: 'Essential governing documents that outline rules and regulations',
    icon: 'file'
  },
  {
    title: 'Insurance Documents',
    importance: 'high',
    items: ['Master Policy', 'Claims History', 'Coverage Details', 'Certificates', 'Endorsements'],
    description: 'Coverage details and historical claims information',
    icon: 'shield'
  },
  {
    title: 'Property Management',
    importance: 'medium',
    items: ['Management Agreement', 'Vendor Contracts', 'Maintenance Schedule', 'Staff Records'],
    description: 'Documents related to property management and operations',
    icon: 'building'
  },
  {
    title: 'Maintenance Records',
    importance: 'medium',
    items: ['Repair History', 'Inspection Reports', 'Work Orders', 'Warranties'],
    description: 'Historical maintenance and repair documentation',
    icon: 'chart-line'
  },
  {
    title: 'Owner Information',
    importance: 'medium',
    items: ['Owner Directory', 'Parking Assignments', 'Pet Registrations', 'Storage Units'],
    description: 'Resident and owner-related documentation',
    icon: 'user'
  },
  {
    title: 'Architectural',
    importance: 'low',
    items: ['Floor Plans', 'Modification Requests', 'Approval Records', 'Design Guidelines'],
    description: 'Architectural and design-related documents',
    icon: 'building'
  },
  {
    title: 'Communications',
    importance: 'low',
    items: ['Newsletters', 'Notices', 'Correspondence', 'Bulletins'],
    description: 'Community communications and notices',
    icon: 'envelope'
  }
];

const featureHighlights = [
  { icon: 'clock', text: '24-Hour Turnaround', color: 'yellow' },
  { icon: 'shield', text: '100% Secure', color: 'green' },
  { icon: 'star', text: '5-Star Rated', color: 'blue' },
  { icon: 'users', text: '450+ Users', color: 'violet' },
];

export default function DocumentReviewLanding() {
  const theme = useMantineTheme();
  const { responsive, isXSmall, isSmall } = useResponsive();
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedFile, setSelectedFile] = React.useState<FileWithPath | null>(null);
  const { state: { twoFactorAuthenticated }, dispatch } = useAuthContext();

  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch({ type: 'OPEN_MODAL' });
  };

  const spacing = {
    section: responsive({
      xs: 60,
      sm: 80,
      md: 100,
      lg: 120,
      xl: 140
    }),
    element: responsive({
      xs: 20,
      sm: 24,
      md: 32,
      lg: 40,
      xl: 48
    })
  };

  const typography = {
    hero: responsive({
      xs: 36,
      sm: 48,
      md: 56,
      lg: 64,
      xl: 72
    }),
    section: responsive({
      xs: 28,
      sm: 32,
      md: 36,
      lg: 40,
      xl: 44
    }),
    subtitle: responsive({
      xs: 16,
      sm: 18,
      md: 20,
      lg: 22,
      xl: 24
    })
  };

  const iconSize = responsive({
    xs: 24,
    sm: 28,
    md: 32,
    lg: 36,
    xl: 40
  });

  return (
    <>
      <SiteHeader scheme="dark" />
      <Box component="main">
        <Box
          pos="relative"
          h={responsive({ xs: 600, sm: 700, md: 800 })}
          style={{
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#000'
          }}
        >
          {/* Background Image */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1
            }}
          >
            <Image
              src="/img/draft-landing-page/header-2.png"
              alt="Condo document review"
              height="100%"
              width="100%"
              fit="cover"
              style={{ opacity: 0.7 }}
            />
          </Box>

          {/* Content Container */}
          <Container 
            size="xl" 
            h="100%" 
            style={{ 
              position: 'relative',
              zIndex: 2
            }}
          >
            <Stack
              justify="center"
              h="100%"
              gap="xl"
              style={{
                padding: '2rem'
              }}
            >
              <Box>
                <Title
                  ta="center"
                  order={1}
                  style={{
                    color: "#fff",
                    fontSize: responsive({ xs: "2.5rem", sm: "3.5rem", md: "4.5rem" }),
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                    textTransform: "uppercase",
                    marginBottom: "1rem",
                    textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                    lineHeight: 1.2
                  }}
                >
                  CONDO DOCUMENTS
                </Title>

                <Title
                  ta="center"
                  order={2}
                  style={{
                    color: "#FFD700",
                    fontSize: responsive({ xs: "1.5rem", sm: "2rem", md: "2.5rem" }),
                    fontWeight: 600,
                    marginBottom: "2rem",
                    textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                    lineHeight: 1.3
                  }}
                >
                  DON&apos;T HAVE TO BE A HEADACHE
                </Title>

                <Text
                  ta="center"
                  style={{
                    color: "#fff",
                    fontSize: responsive({ xs: "1.1rem", sm: "1.25rem", md: "1.5rem" }),
                    maxWidth: "800px",
                    margin: "0 auto",
                    marginBottom: "2.5rem",
                    textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
                    lineHeight: 1.5,
                    fontWeight: 500
                  }}
                >
                  Experience lightning-fast, AI-powered document reviews that save you time and eliminate hassle
                </Text>
              </Box>

              <Group justify="center" gap="md">
                <Button
                  size="large"
                  onClick={handleLoginClick}
                  variant="filled"
                  component="button"
                  transition={false}
                  style={{
                    fontSize: "1.25rem",
                    padding: "1.5rem 3rem",
                    backgroundColor: "#FFD700",
                    color: "#000",
                    fontWeight: 600
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  size="large"
                  variant="outline"
                  onClick={handleLoginClick}
                  component="button"
                  transition={false}
                  style={{
                    fontSize: "1.25rem",
                    padding: "1.5rem 3rem",
                    color: "#fff",
                    borderColor: "#fff",
                    borderWidth: "2px",
                    fontWeight: 600
                  }}
                >
                  Watch Demo
                </Button>
              </Group>

              <Grid gutter="lg">
                {featureHighlights.map((feature, index) => (
                  <Grid.Col key={index} span={{ base: 6, sm: 3 }}>
                    <Paper
                      p="lg"
                      style={{
                        background: "rgba(255, 255, 255, 0.1)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        borderRadius: "12px",
                        transition: "transform 0.3s ease, background-color 0.3s ease",
                        cursor: "pointer",
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          background: "rgba(255, 255, 255, 0.15)"
                        }
                      }}
                    >
                      <Group gap="md" justify="center">
                        <FAIcon
                          name={feature.icon as ValidIconName}
                          size={32}
                          style={{
                            color: '#FFD700',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                          }}
                        />
                        <Text
                          style={{
                            color: '#fff',
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                          }}
                        >
                          {feature.text}
                        </Text>
                      </Group>
                    </Paper>
                  </Grid.Col>
                ))}
              </Grid>
            </Stack>
          </Container>
        </Box>

        <Container size="xl" py={spacing.section}>
          <Stack gap={spacing.element}>
            <Title ta="center" order={2} size={typography.section}>
              What Our Users Say
            </Title>
            <CustomCarousel posts={socialPosts} />
          </Stack>
        </Container>

        <Box bg="gray.0" py={spacing.section}>
          <Container size="xl">
            <Grid gutter={spacing.element}>
              {[
                {
                  icon: "clock",
                  title: "Rapid Processing",
                  description: "Get your documents reviewed quickly and efficiently",
                  color: "blue"
                },
                {
                  icon: "dollar",
                  title: "Affordable Rates",
                  description: "Competitive pricing without compromising quality",
                  color: "green"
                },
                {
                  icon: "check-circle",
                  title: "Guaranteed Precision",
                  description: "Thorough review process ensuring accuracy",
                  color: "violet"
                }
              ].map((benefit, index) => (
                <Grid.Col key={index} span={{ base: 12, sm: 6, md: 4 }}>
                  <Card
                    shadow="sm"
                    p={spacing.element}
                    radius="md"
                    style={{
                      height: '100%',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: theme.shadows.md
                      }
                    }}
                  >
                    <Stack align="center" gap="md">
                      <FAIcon
                        name={benefit.icon as ValidIconName}
                        size={iconSize}
                        color={theme.colors[benefit.color][6]}
                      />
                      <Title order={3} size={typography.subtitle} ta="center">
                        {benefit.title}
                      </Title>
                      <Text size="md" ta="center" c="gray.7">
                        {benefit.description}
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>
              ))}
            </Grid>
          </Container>
        </Box>

        <Container size="xl" py={spacing.section}>
          <Stack gap={spacing.element}>
            <Title ta="center" order={2} size={typography.section}>
              How It Works
            </Title>
            <CustomAccordion
              data={[
                {
                  title: "Step 1: Sign Up",
                  content: (
                    <Group gap="sm">
                      <FAIcon name="user" size={24} />
                      <Text size="lg">Create your account and verify your email to get started. Our streamlined process takes less than 2 minutes.</Text>
                    </Group>
                  )
                },
                {
                  title: "Step 2: Upload & Review Documents",
                  content: (
                    <Group gap="sm">
                      <FAIcon name="upload" size={24} />
                      <Text size="lg">Upload your documents using our intuitive interface. Our system supports multiple file formats and provides instant validation.</Text>
                    </Group>
                  )
                },
                {
                  title: "Step 3: Leverage Platform Features",
                  content: (
                    <Group gap="sm">
                      <FAIcon name="chart-line" size={24} />
                      <Text size="lg">Take advantage of our advanced features including automated analysis, custom checklists, and detailed reporting.</Text>
                    </Group>
                  )
                }
              ]}
            />
          </Stack>
        </Container>

        <Box bg="gray.0" py={spacing.section}>
          <Container size="xl">
            <Stack gap={spacing.element}>
              <Title ta="center" order={2} size={typography.section}>
                Document Upload Process
              </Title>
              <Grid>
                <Grid.Col span={{ base: 12, md: 8 }}>
                  <Paper
                    p={spacing.element}
                    radius="lg"
                    style={{
                      background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                      border: "2px dashed #adb5bd",
                      overflow: "hidden"
                    }}
                  >
                    <Stack align="center" gap={spacing.element}>
                      <Box
                        style={{
                          background: theme.colors.blue[6],
                          borderRadius: "50%",
                          padding: "20px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                        }}
                      >
                        <FAIcon name="upload" size={iconSize} color="white" />
                      </Box>
                      <Stack gap="xs" align="center">
                        <Title order={3} size={typography.subtitle}>
                          Secure Document Upload
                        </Title>
                        <Text c="gray.7" size="lg" ta="center">
                          Our platform accepts PDF documents up to 100MB
                        </Text>
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Card shadow="sm" p={spacing.element} radius="lg" style={{ height: '100%' }}>
                    <Stack gap="md">
                      <Title order={3} size={typography.subtitle}>
                        Sample Documents
                      </Title>
                      <Text size="md" c="gray.7">
                        Review our sample documents to understand the required format and content:
                      </Text>
                      <Stack gap="xs">
                        <Text size="md" fw={500}>&bull; Sample CC&amp;Rs</Text>
                        <Text size="md" fw={500}>&bull; Sample Bylaws</Text>
                        <Text size="md" fw={500}>&bull; Document Checklist</Text>
                      </Stack>
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>
            </Stack>
          </Container>
        </Box>

        <Container size="xl" py={spacing.section}>
          <Stack gap={spacing.element}>
            <Title ta="center" order={2} size={typography.section}>
              Document Checklist
            </Title>
            <CustomAccordion
              data={documentTypes.map((docType) => ({
                title: (
                  <Group gap="sm">
                    <FAIcon name={docType.icon} size={24} />
                    <Text size="lg" fw={500}>{docType.title}</Text>
                    <Badge
                      size="lg"
                      variant="filled"
                      color={docType.importance === 'high' ? 'red' : 'blue'}
                      style={{ textTransform: 'capitalize' }}
                    >
                      {docType.importance}
                    </Badge>
                  </Group>
                ),
                content: (
                  <Stack gap="md" p="md">
                    {docType.items.map((item, idx) => (
                      <Tooltip key={idx} label={docType.description}>
                        <Text size="md">• {item}</Text>
                      </Tooltip>
                    ))}
                  </Stack>
                )
              }))}
            />
          </Stack>
        </Container>

        <Box bg="gray.0" py={spacing.section}>
          <Container size="xl">
            <Card
              shadow="lg"
              p={spacing.section}
              radius="lg"
              style={{
                background: 'linear-gradient(135deg, #0055ff 0%, #00a3ff 100%)',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                padding: '4rem 2rem'
              }}
            >
              <Stack align="center" gap="xl" style={{ width: '100%', maxWidth: '800px' }}>
                <Title 
                  ta="center" 
                  order={2} 
                  c="white" 
                  size={typography.section}
                  style={{
                    fontSize: responsive({ xs: '2.5rem', sm: '3rem', md: '3.5rem' }),
                    fontWeight: 700,
                    marginBottom: '1rem',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  Ready to Get Started?
                </Title>
                <Text 
                  ta="center" 
                  size={typography.subtitle} 
                  style={{ 
                    opacity: 0.9,
                    fontSize: responsive({ xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }),
                    lineHeight: 1.5,
                    marginBottom: '2rem',
                    maxWidth: '600px'
                  }}
                >
                  Join thousands of satisfied users who have transformed their condo document review process
                </Text>
                <Button
                  size="large"
                  radius="md"
                  variant="filled"
                  onClick={handleLoginClick}
                  transition={false}
                  style={{
                    backgroundColor: '#FFD700',
                    color: '#000',
                    fontSize: '1.25rem',
                    padding: '1.5rem 3rem',
                    fontWeight: 600
                  }}
                  component='button'
                >
                  Register Now
                </Button>
              </Stack>
            </Card>
          </Container>
        </Box>
      </Box>
      <HelpChangeSection/>
      <SiteFooter />
    </>
  );
} 